import {Component, EventEmitter, Output, computed, inject, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ISupportRequest} from '../../../../../interfaces/player/ISupportRequest';
import {SupportDeskColDefs} from '../../../support-desk-column-defs';

@Component({
  selector: 'app-ticket-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './ticket-card.component.html',
  styleUrl: './ticket-card.component.scss'
})
export class TicketCardComponent {
  private supportDeskColDefs = inject(SupportDeskColDefs);

  // Signal inputs
  ticket = input.required<ISupportRequest>();
  showQuickActions = input(true);
  isSelected = input(false);

  @Output() cardClicked = new EventEmitter<ISupportRequest>();
  @Output() assignToMe = new EventEmitter<ISupportRequest>();

  // Computed signals
  errorTypeLabel = computed(() => {
    const errorType = this.supportDeskColDefs.errorTypes.find(e => e.value === this.ticket().ErrorType);
    return errorType?.name ?? 'Unknown';
  });

  errorTypeIcon = computed(() => {
    switch (this.ticket().ErrorType) {
      case 1: return 'email'; // Not receiving emails
      case 2: return 'lock'; // Cannot login
      case 3: return 'person_add'; // Cannot signup
      case 4: return 'confirmation_number'; // Cannot play tickets
      case 5: return 'credit_card'; // Cannot update payment
      case 6: return 'volunteer_activism'; // Cannot update charity
      case 7: return 'password'; // Cannot update password
      case 8: return 'alternate_email'; // Cannot update email
      case 9: return 'person'; // Cannot update personal info
      case 10: return 'help_outline'; // Other
      case 11: return 'cancel'; // Deactivate numbers
      default: return 'help_outline';
    }
  });

  timeAgo = computed(() => {
    const now = new Date();
    const created = new Date(this.ticket().CreatedOn);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMins > 0) {
      return `${diffMins}m ago`;
    } else {
      return 'Just now';
    }
  });

  urgencyClass = computed(() => {
    const now = new Date();
    const created = new Date(this.ticket().CreatedOn);
    const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);

    if (diffHours > 48) {
      return 'urgency-high';
    } else if (diffHours > 24) {
      return 'urgency-medium';
    } else {
      return 'urgency-low';
    }
  });

  onCardClick(): void {
    this.cardClicked.emit(this.ticket());
  }

  onAssignToMeClick(event: Event): void {
    event.stopPropagation();
    this.assignToMe.emit(this.ticket());
  }
}
